import React, { useEffect, useState } from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Text,
  Heading,
  Stack,
  IconButton,
  Button,
  Input,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  Menu,
  MenuItem,
  MenuButton,
  MenuList,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Select,
  useColorModeValue
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { cqAPI, Auth } from "../../lib/Auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import dialogManager from "../DialogManager/DialogManager";
import EditableText from "../EditableText/EditableText";
import ReactSelect from "react-select";
import moment from 'moment';

// SS-4511: a Group extension rings its members (other extensions of the practice) all at once
export const GROUP_TYPE = "group";
// Twilio rings at most 10 destinations at once
export const MAX_GROUP_MEMBERS = 10;
export const GROUP_LIMITATIONS_NOTE =
  "Rings all members at once until someone answers or the caller hangs up. " +
  "Can only be reached by dialing it from an office phone or by transferring a call to it. " +
  "Not available in call routing or to the AI. No voicemail.";

const isGroup = (device) => device?.device_type === GROUP_TYPE;

const memberLabel = (numericValue, name) => `${numericValue}${name ? ` - ${name}` : ""}`;

/** The extensions a group can ring: every extension of the practice but a group, shown as number + name. */
export const groupMemberOptions = (devices) =>
  (devices || [])
    .filter((device) => !isGroup(device) && device.extension?._id)
    .map((device) => ({
      value: device.extension._id,
      label: memberLabel(device.extension.numeric_value, device.name),
    }));

/** A multi-select of the practice's non-group extensions, at most MAX_GROUP_MEMBERS of them. */
const GroupMembersSelect = ({ options, value, onChange }) => (
  <ReactSelect
    isMulti
    inputId="group-members"
    placeholder={`Choose up to ${MAX_GROUP_MEMBERS} extensions`}
    options={options}
    value={options.filter((option) => value.includes(option.value))}
    isOptionDisabled={(_option, selected) => selected.length >= MAX_GROUP_MEMBERS}
    onChange={(selected) => onChange((selected || []).map((option) => option.value))}
    styles={{ menu: (base) => ({ ...base, zIndex: 2 }) }}
  />
);

const validGroupMembers = (members) => members.length >= 1 && members.length <= MAX_GROUP_MEMBERS;

function PhoneExtensions() {
  const [devices, setDevices] = useState([]);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [newExtension, setNewExtension] = useState({
    type: "hw", // 'Hardware', 'Phone Number' or 'Group'
    address: "",
    extension: "",
    name: "",
    sn: "",
    members: [],
  });
  // the group whose members are being edited: { _id, members: [extension ids] }
  const [editingGroup, setEditingGroup] = useState(null);
  // the api says it supports groups (an older one would store a Group as a phone number extension)
  const [groupsSupported, setGroupsSupported] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure({
    onClose: () => {
      setNewExtension({
        type: "hw"
      });
      setIsAlertVisible(false);
    }
  });
  const headerBackgroundColor = useColorModeValue('#F1F1F1', '#303343');
  const backgroundColor = useColorModeValue('#FFFFFF', '#212330');
  const hoverBackgroundColor = useColorModeValue('#212330', '#FFFFFF');

  const fetchExtensions = async () => {
    try {
      const response = await cqAPI.get("/settings/phone/extensions");
      setDevices(response.data);
      setGroupsSupported(response?.group_extensions === true);
    } catch (error) {
      console.error("Error fetching phone extensions:", error);
    }
  };

  useEffect(() => {
    fetchExtensions();
  }, []);

  const validatePhoneNumber = (phoneNumber) => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/; // Simplified regex for phone numbers, adjust as needed
    return phoneRegex.test(phoneNumber);
  };

  const handleSave = async (deviceId, field, newValue) => {
    // Update your state and optionally send update to server here
    let updatedExtension;
    const updatedDevices = devices.map((device) => {
      if (device._id === deviceId) {
        if (field === "extension") {
          if (!/^\d{2,4}$/.test(newValue)) {
            setAlertMessage("The extension is a number between 10-9999.");
            setIsAlertVisible(true);
            return device;
          }
          updatedExtension = device;
          return {
            ...device,
            extension: { ...(device.extension || {}), numeric_value: newValue },
          };
        }
        if (field === "address") {
          if (device.device_type === "hw") {
            //this means the address should be a mac address
            if (newValue && !/^[a-f0-9]{4,}$/i.test(newValue)) {
              setAlertMessage("Please enter a valid MAC Address.");
              setIsAlertVisible(true);
              return device;
            }
          } else {
            //this means that the address should be a phone number
            if (!validatePhoneNumber(newValue)) {
              setAlertMessage("Please enter a valid phone number.");
              setIsAlertVisible(true);
              return device;
            }
          }
          updatedExtension = device;
          return { ...device, address: newValue };
        }
        if (field === "name") {
          if ((newValue || '').trim() === '') {
            setAlertMessage("Name cannot be empty.");
            setIsAlertVisible(true);
            return device;
          }
          updatedExtension = device;
          return { ...device, name: newValue };
        }
      }
      return { ...device };
    });
    if (!updatedExtension) {
      return false;
    }
    const updated = await cqAPI
      .put("/settings/phone/extensions", {
        eid: updatedExtension._id,
        field,
        value: newValue,
      })
      .then((e) => {
        if (e.type === "ok") {
          return true;
        }
        if (e.msg) {
          dialogManager.open({
            onDeny: () => { },
            acceptText: "OK",
            body: e.msg,
            header: "Error",
          });
          return;
        }
        return false;
      })
      .catch((e) => false);
    if (updated) {
      setDevices(updatedDevices);
      await fetchExtensions();
      return true;
    } else {
      setDevices(devices);
      return false;
    }
  };

  const addNewExtension = async () => {
    if (!/^\d{2,4}$/.test(newExtension.extension)) {
      setAlertMessage("The extension is a number between 10-9999.");
      setIsAlertVisible(true);
      return;
    }

    if (newExtension.type === GROUP_TYPE) {
      if (!validGroupMembers(newExtension.members || [])) {
        setAlertMessage(`Choose between 1 and ${MAX_GROUP_MEMBERS} member extensions.`);
        setIsAlertVisible(true);
        return;
      }
    } else if (newExtension.type === "hw") {
      //this means the address should be a mac address
      if (!Auth?.user?.isSuperAdmin) {
        if (!/^[a-f0-9]{4,}$/i.test(newExtension.address || '')) {
          setAlertMessage("Please enter a valid MAC Address.");
          setIsAlertVisible(true);
          return;
        }
        if (!newExtension.sn || newExtension.sn.trim() === '') {
          setAlertMessage("Please enter a Serial Number.");
          setIsAlertVisible(true);
          return;
        }
      }
    } else {
      //this means that the address should be a phone number
      if (!validatePhoneNumber(newExtension.address)) {
        setAlertMessage("Please enter a valid phone number.");
        setIsAlertVisible(true);
        return;
      }
    }

    if ((newExtension.name || '').trim() === '') {
      setAlertMessage("Name cannot be empty.");
      setIsAlertVisible(true);
      return;
    }

    const resp = await cqAPI.post("/settings/phone/extensions", newExtension.type === GROUP_TYPE ? {
      type: GROUP_TYPE,
      name: newExtension.name,
      extension: newExtension.extension,
      members: newExtension.members,
    } : {
      type: newExtension.type,
      name: newExtension.name,
      extension: newExtension.extension,
      address: newExtension.address,
      sn: newExtension.sn,
    });
    if (!resp) {
      return;
    }
    if (resp.msg) {
      dialogManager.open({
        onDeny: () => { },
        acceptText: "OK",
        body: resp.msg,
        header: "Error",
      });
      return;
    }
    if (resp.type === "ok") {
      await fetchExtensions();
      onClose();
      setNewExtension({
        type: "hw",
        address: "",
        extension: "",
        name: "",
        sn: "",
        members: [],
      });
    }
  };

  const saveGroupMembers = async () => {
    if (!validGroupMembers(editingGroup.members)) {
      setAlertMessage(`Choose between 1 and ${MAX_GROUP_MEMBERS} member extensions.`);
      setIsAlertVisible(true);
      return;
    }
    const resp = await cqAPI.put("/settings/phone/extensions", {
      eid: editingGroup._id,
      field: "members",
      value: editingGroup.members,
    });
    if (!resp) {
      return;
    }
    if (resp.msg) {
      dialogManager.open({
        onDeny: () => { },
        acceptText: "OK",
        body: resp.msg,
        header: "Error",
      });
      return;
    }
    if (resp.type === "ok") {
      setEditingGroup(null);
      await fetchExtensions();
    }
  };

  const deleteExtension = async (device) => {
    if (!device.extension) {
      setAlertMessage("This is a legacy device. Contact support to remove it.");
      setIsAlertVisible(true);
      return;
    }
    const resp = await cqAPI.del("/settings/phone/extensions", {
      eid: device.extension._id,
    });
    if (!resp) {
      return;
    }
    if (resp.msg) {
      dialogManager.open({
        onDeny: () => { },
        acceptText: "OK",
        body: resp.msg,
        header: "Error",
      });
      return;
    }
    if (resp.type === "ok") {
      await fetchExtensions();
    }
  };

  const getHardwarePlaceholder = (device) => device.device_type === "hw" ? "MAC Address" : "Phone Number";

  const addExtensionModal = (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add New Extension</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel>Type</FormLabel>
            <Select
              value={newExtension.type}
              onChange={(e) => {
                setNewExtension({ ...newExtension, type: e.target.value });
                setIsAlertVisible(false);
              }
              }
            >
              <option value="hw">Hardware</option>
              <option value="did">Phone Number</option>
              {groupsSupported && <option value={GROUP_TYPE}>Group</option>}
            </Select>
          </FormControl>
          <FormControl mt={4}>
            <FormLabel>Name</FormLabel>
            <Input
              placeholder="Name"
              value={newExtension.name}
              onChange={(e) => {
                if (e.target.value.length === 0) {
                  setNewExtension({ ...newExtension, name: e.target.value });
                  setAlertMessage("The name cannot be empty.");
                  setIsAlertVisible(true);
                  return
                } else if (e.target.value.length > 15) {
                  setAlertMessage("The name can be up to 15 characters.");
                  setIsAlertVisible(true);
                  return
                } else {
                  setNewExtension({ ...newExtension, name: e.target.value });
                  setIsAlertVisible(false);
                }


              }
              }
            />
          </FormControl>
          {newExtension.type === "hw" && (
            <FormControl mt={4} isRequired={!Auth?.user?.isSuperAdmin}>
              <FormLabel>MAC Address</FormLabel>
              <Input
                placeholder="MAC Address"
                value={newExtension.address}
                onChange={(e) => {
                  setNewExtension({ ...newExtension, address: e.target.value });
                  setIsAlertVisible(false);
                }
                }
              />
            </FormControl>
          )}
          {newExtension.type === "hw" && (
            <FormControl mt={4} isRequired={!Auth?.user?.isSuperAdmin}>
              <FormLabel>Serial Number</FormLabel>
              <Input
                placeholder="Serial Number"
                value={newExtension.sn}
                onChange={(e) => {
                  setNewExtension({ ...newExtension, sn: e.target.value });
                  setIsAlertVisible(false);
                }
                }
              />
            </FormControl>
          )}
          {newExtension.type === "did" && (
            <FormControl mt={4}>
              <FormLabel>Phone Number</FormLabel>
              <Input
                placeholder="Phone Number"
                value={newExtension.address}
                onChange={(e) => {
                  setNewExtension({ ...newExtension, address: e.target.value });
                  setIsAlertVisible(false);
                }
                }
              />
            </FormControl>
          )}
          <FormControl mt={4}>
            <FormLabel>Extension</FormLabel>
            <Input
              placeholder="Extension"
              type="number"
              value={newExtension.extension}
              onChange={(e) => {
                setNewExtension({ ...newExtension, extension: e.target.value });
                setIsAlertVisible(false);
              }
              }
            />
          </FormControl>
          {newExtension.type === GROUP_TYPE && (
            <FormControl mt={4}>
              <FormLabel htmlFor="group-members">Members</FormLabel>
              <GroupMembersSelect
                options={groupMemberOptions(devices)}
                value={newExtension.members || []}
                onChange={(members) => {
                  setNewExtension({ ...newExtension, members });
                  setIsAlertVisible(false);
                }}
              />
              <Text mt={2} fontSize="sm">{GROUP_LIMITATIONS_NOTE}</Text>
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={addNewExtension}>
            Save
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  const editGroupMembersModal = (
    <Modal isOpen={Boolean(editingGroup)} onClose={() => setEditingGroup(null)}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Group Members</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel htmlFor="group-members">Members</FormLabel>
            <GroupMembersSelect
              options={groupMemberOptions(devices)}
              value={editingGroup?.members || []}
              onChange={(members) => {
                setEditingGroup({ ...editingGroup, members });
                setIsAlertVisible(false);
              }}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={saveGroupMembers}>
            Save
          </Button>
          <Button variant="ghost" onClick={() => setEditingGroup(null)}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
      position="relative"
    >
      {addExtensionModal}
      {editGroupMembersModal}

      {isAlertVisible && (
        <Box position="fixed" top="0" left="0" right="0" zIndex="1500">
          <Alert status="error" bg="red.500">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription display="block">
                {alertMessage}
              </AlertDescription>
            </Box>
            <CloseButton
              position="absolute"
              right="8px"
              top="8px"
              onClick={() => setIsAlertVisible(false)}
            />
          </Alert>
        </Box>
      )}
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Heading size="lg">Phone Extensions</Heading>
        <Button leftIcon={<AddIcon />} onClick={onOpen}>
          Add Extension
        </Button>
      </Flex>
      <SimpleGrid columns={{ base: 1, md: 3, lg: 3 }} spacing="20px">
        {(devices || []).map((device, index) => (
          <Box
            key={index}
            className="phone-card"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            p={4}
            bg={backgroundColor}
            boxShadow='base'
          >
            <Flex
              className="card-header"
              p={4}
              bg={headerBackgroundColor}
            >
              <EditableText
                initialValue={device.name}
                onSave={(e) => handleSave(device._id, "name", e)}
                showPlaceholderInDisplayMode={false}
                placeholder="Name"
                pattern={/^.{1,40}$/}
                invalid="The name can be up to 40 characters and cannot be empty."
              />
              <Menu>
                <MenuButton
                  as={IconButton}
                  icon={<FontAwesomeIcon icon={faEllipsisV} />}
                  variant="ghost"
                  bg={headerBackgroundColor}
                  _hover={{ bg: hoverBackgroundColor, color: backgroundColor }}
                />
                <MenuList>
                  {isGroup(device) && (
                    <MenuItem
                      onClick={() => setEditingGroup({
                        _id: device._id,
                        members: (device.members || []).map((member) => member._id),
                      })}
                    >
                      Edit Members
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => deleteExtension(device)}>
                    Delete
                  </MenuItem>
                </MenuList>
              </Menu>
            </Flex>
            <Flex className="card-body" flexDirection="column" flex="1" p={4}>
              <Stack spacing={3} textAlign="left">
                {/* Extension Field */}
                <Flex alignItems="center">
                  <Text mr={2}>Extension</Text>
                  <EditableText
                    initialValue={device.extension?.numeric_value}
                    onSave={(e) => handleSave(device._id, "extension", e)}
                    showPlaceholderInDisplayMode={false}
                    placeholder="Extension"
                  />
                </Flex>

                {isGroup(device) ? (
                  <>
                    {/* Group members (SS-4511) */}
                    <Box className="group-members">
                      <Text mr={2}>Members</Text>
                      {(device.members || []).length ? (
                        (device.members || []).map((member) => (
                          <Text key={member._id} fontSize="sm" color="gray.600">
                            {memberLabel(member.numeric_value, member.name)}
                          </Text>
                        ))
                      ) : (
                        <Text fontSize="sm" color="gray.600">No members</Text>
                      )}
                    </Box>
                    <Button
                      size="sm"
                      alignSelf="flex-start"
                      onClick={() => setEditingGroup({
                        _id: device._id,
                        members: (device.members || []).map((member) => member._id),
                      })}
                    >
                      Edit Members
                    </Button>
                    <Text className="group-limitations" fontSize="12px">
                      {GROUP_LIMITATIONS_NOTE}
                    </Text>
                  </>
                ) : (
                  <>
                    {/* Address Field */}
                    <Flex alignItems="center">
                      <Text mr={2}>
                        {getHardwarePlaceholder(device)}
                      </Text>
                      <EditableText
                        initialValue={device.address}
                        onSave={(e) => handleSave(device._id, "address", e)}
                        showPlaceholderInDisplayMode={false}
                        placeholder={getHardwarePlaceholder(device)}
                      />
                    </Flex>
                    {/* Serial Number Field (for hardware devices) */}
                    {device.device_type === "hw" && device.sn && (
                      <Flex alignItems="center">
                        <Text mr={2}>Serial Number</Text>
                        <Text fontSize="sm" color="gray.600">{device.sn}</Text>
                      </Flex>
                    )}
                    <Flex alignItems="center">
                      <Text mr={2} fontSize="12px">
                        {device.last_provisioned ? `Last Provisioned on ${moment(device.last_provisioned).format('LLL')}` : 'Not provisioned'}
                      </Text>
                    </Flex>
                  </>
                )}
              </Stack>
            </Flex>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
}

export default PhoneExtensions;
